import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';

export interface FocusTermHighlightOptions {
  focusTerms: string[];
  highlightClass: string;
  onTermCountChange?: (counts: Record<string, number>) => void;
}

export const FocusTermHighlightPluginKey = new PluginKey('focusTermHighlight');

// Store last term counts to avoid unnecessary callbacks
let lastTermCountsJSON = '';

export const FocusTermHighlight = Extension.create<FocusTermHighlightOptions>({
  name: 'focusTermHighlight',

  addOptions() {
    return {
      focusTerms: [],
      highlightClass: 'focus-term-highlight',
      onTermCountChange: undefined,
    };
  },

  addStorage() {
    return {
      focusTerms: this.options.focusTerms,
    };
  },

  addCommands() {
    return {
      setFocusTerms:
        (terms: string[]) =>
        ({ editor }: { editor: import('@tiptap/core').Editor }) => {
          this.storage.focusTerms = terms;
          // Force re-decoration
          editor.view.dispatch(editor.view.state.tr.setMeta(FocusTermHighlightPluginKey, { terms }));
          return true;
        },
    } as Record<string, unknown>;
  },

  addProseMirrorPlugins() {
    const extensionOptions = this.options;
    const storage = this.storage;

    return [
      new Plugin({
        key: FocusTermHighlightPluginKey,
        state: {
          init: (_, state) => {
            return buildDecorations(state.doc, storage.focusTerms, extensionOptions);
          },
          apply: (tr, oldDecorations, _oldState, newState) => {
            // Check if focus terms were updated via setMeta
            const meta = tr.getMeta(FocusTermHighlightPluginKey);
            if (meta?.terms) {
              storage.focusTerms = meta.terms;
            }

            // Rebuild decorations if doc changed or terms updated
            if (tr.docChanged || meta?.terms) {
              return buildDecorations(newState.doc, storage.focusTerms, extensionOptions);
            }

            return oldDecorations.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

function buildDecorations(
  doc: ProseMirrorNode,
  focusTerms: string[],
  options: FocusTermHighlightOptions
): DecorationSet {
  const decorations: Decoration[] = [];
  const termCounts: Record<string, number> = {};

  // Initialize counts
  focusTerms.forEach((term) => {
    termCounts[term.toLowerCase()] = 0;
  });

  if (focusTerms.length === 0) {
    // Only notify if counts actually changed
    notifyTermCountsIfChanged(termCounts, options.onTermCountChange);
    return DecorationSet.create(doc, []);
  }

  // Build regex pattern for all terms
  const escapedTerms = focusTerms.map((term) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  const pattern = new RegExp(`\\b(${escapedTerms.join('|')})\\b`, 'gi');

  // Walk through document and find text nodes
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) {
      return;
    }

    // Reset lastIndex to avoid issues with global regex
    pattern.lastIndex = 0;

    let match;
    while ((match = pattern.exec(node.text)) !== null) {
      const start = pos + match.index;
      const end = start + match[0].length;
      const matchedTerm = match[1].toLowerCase();

      // Update count
      if (termCounts[matchedTerm] !== undefined) {
        termCounts[matchedTerm]++;
      }

      // Add decoration
      decorations.push(
        Decoration.inline(start, end, {
          class: options.highlightClass,
          'data-focus-term': matchedTerm,
        })
      );
    }
  });

  // Notify about term counts only if changed (async to avoid side effects during render)
  notifyTermCountsIfChanged(termCounts, options.onTermCountChange);

  return DecorationSet.create(doc, decorations);
}

/**
 * Notify about term count changes only if they actually changed.
 * Uses setTimeout to defer the callback and avoid side effects during render.
 */
function notifyTermCountsIfChanged(
  termCounts: Record<string, number>,
  onTermCountChange?: (counts: Record<string, number>) => void
): void {
  if (!onTermCountChange) return;

  const newJSON = JSON.stringify(termCounts);
  if (newJSON !== lastTermCountsJSON) {
    lastTermCountsJSON = newJSON;
    // Defer callback to avoid side effects during render
    setTimeout(() => onTermCountChange(termCounts), 0);
  }
}

export default FocusTermHighlight;
